import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Trash2, Clock, PoundSterling, Edit2, Check, Sparkles } from 'lucide-react';
import { CATEGORIES, getCategoryName } from '../../lib/categories';

const CATEGORY_LABEL_MAP = Object.fromEntries(CATEGORIES.map((c) => [c.id, c.name]));

function formatDuration(mins) {
  if (mins >= 60) {
    const hrs = Math.floor(mins / 60);
    const rem = mins % 60;
    return rem ? `${hrs}h ${rem}m` : `${hrs}h`;
  }
  return `${mins} min`;
}

export function ServicesStep({ form, setForm }) {
  const services = form.draft_services || [];
  const [editing, setEditing] = useState(null); // index or 'new'
  const [draft, setDraft] = useState(null);

  // Templates auto-added when a category was ticked in the previous step
  // appear at the top as "Recommended starter services". The provider can
  // untick them, edit price/duration inline, or fall back to the manual
  // editor for the bigger fields.
  const templateServices = services
    .map((s, i) => ({ ...s, _idx: i }))
    .filter((s) => s._from_template);
  const customServices = services
    .map((s, i) => ({ ...s, _idx: i }))
    .filter((s) => !s._from_template);

  // Group templates by category so a stylist who ticked 3 pillars sees
  // 3 mini-sections with clear headings.
  const templatesByCategory = templateServices.reduce((acc, s) => {
    (acc[s.category] = acc[s.category] || []).push(s);
    return acc;
  }, {});

  const startNew = () => {
    setDraft({
      name: '',
      description: '',
      duration: 60,
      price: '',
      category: form.categories?.[0] || '',
      service_types: form.service_types || [],
    });
    setEditing('new');
  };

  const startEdit = (index) => {
    setDraft({ ...services[index], price: String(services[index].price) });
    setEditing(index);
  };

  const cancelEdit = () => {
    setDraft(null);
    setEditing(null);
  };

  const saveService = () => {
    if (!draft.name || !draft.price || !draft.category) return;
    const parsed = { ...draft, price: parseFloat(draft.price) || 0 };
    // Modal-edited entries are flagged so an upstream category-untick
    // doesn't accidentally drop a service the provider has explicitly
    // customised.
    if (parsed._from_template) parsed._edited = true;
    const updated = [...services];
    if (editing === 'new') {
      updated.push(parsed);
    } else {
      updated[editing] = parsed;
    }
    setForm((prev) => ({ ...prev, draft_services: updated }));
    setDraft(null);
    setEditing(null);
  };

  const removeService = (index) => {
    const updated = services.filter((_, i) => i !== index);
    setForm((prev) => ({ ...prev, draft_services: updated }));
  };

  // Inline edit on a template card, no modal. Marks the service as
  // explicitly customised so it survives a later category-untick.
  const patchTemplate = (index, patch) => {
    setForm((prev) => {
      const next = [...(prev.draft_services || [])];
      next[index] = { ...next[index], ...patch, _edited: true };
      return { ...prev, draft_services: next };
    });
  };

  // Re-add a previously-removed template. We synthesise it from the form's
  // selected categories so the provider can resurrect any default they
  // earlier dropped without restarting the flow. Lives in the "below the
  // active list" footer.
  // (Kept simple: only show the toggle for templates that aren't currently
  // present, computed once per render.)

  return (
    <div data-testid="services-step">
      <h2 className="font-heading text-2xl font-medium text-[#1A1A1A] mb-1">Add Your Services</h2>
      <p className="text-stone-500 text-sm mb-6">
        We&apos;ve pre-filled the most-booked services for each pillar you picked. Untick anything you don&apos;t offer, edit the price or duration inline, then add anything bespoke at the bottom.
      </p>

      {/* Recommended starter services, grouped by category */}
      {Object.keys(templatesByCategory).length > 0 && (
        <div className="space-y-6 mb-6" data-testid="template-services">
          {Object.entries(templatesByCategory).map(([catId, items]) => (
            <div key={catId}>
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-4 w-4 text-[#D4AF37]" />
                <h3 className="text-sm font-semibold text-[#1A1A1A]">
                  Starter services, {CATEGORY_LABEL_MAP[catId] || getCategoryName(catId)}
                </h3>
              </div>
              <div className="space-y-2">
                {items.map((s) => (
                  <label
                    key={s._template_id || `${s.name}-${s._idx}`}
                    className="flex items-center gap-3 p-3 bg-[#F9F6F0] border border-[#E6E4DD] rounded-lg cursor-pointer hover:border-[#D4AF37] transition-colors"
                    data-testid={`template-service-${s._template_id || s._idx}`}
                  >
                    <input
                      type="checkbox"
                      checked
                      onChange={() => removeService(s._idx)}
                      className="h-4 w-4 rounded border-[#D4AF37] text-[#D4AF37] focus:ring-[#D4AF37]"
                      data-testid={`template-toggle-${s._template_id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#1A1A1A] truncate">{s.name}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <PoundSterling className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-stone-400 pointer-events-none" />
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={s.price}
                          onChange={(e) => {
                            const v = e.target.value;
                            patchTemplate(s._idx, { price: v === '' ? '' : parseFloat(v) || 0 });
                          }}
                          onClick={(e) => e.preventDefault()}
                          className="w-24 h-8 text-sm pl-6 input-zuriya"
                          data-testid={`template-price-${s._template_id}`}
                        />
                      </div>
                      <Select
                        value={String(s.duration)}
                        onValueChange={(v) => patchTemplate(s._idx, { duration: parseInt(v, 10) })}
                      >
                        <SelectTrigger
                          className="w-24 h-8 text-sm input-zuriya"
                          onClick={(e) => e.preventDefault()}
                          data-testid={`template-duration-${s._template_id}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[15, 30, 45, 60, 75, 90, 105, 120, 150, 180, 210, 240, 270, 300, 360, 420, 480, 540, 600, 660, 720].map((m) => (
                            <SelectItem key={m} value={String(m)}>{formatDuration(m)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Custom services (manually added by the provider) */}
      {customServices.length > 0 && (
        <div className="space-y-3 mb-6">
          <h3 className="text-sm font-semibold text-[#1A1A1A]">Your custom services</h3>
          {customServices.map((svc) => (
            <div
              key={`${svc.name}-${svc.category}-${svc._idx}`}
              className="flex items-center gap-3 p-4 bg-[#F9F6F0] border border-[#E6E4DD] rounded-lg group"
              data-testid={`draft-service-${svc._idx}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-medium text-[#1A1A1A] text-sm">{svc.name}</h4>
                  <span className="text-xs px-2 py-0.5 bg-white rounded-full text-stone-500">
                    {CATEGORY_LABEL_MAP[svc.category] || svc.category}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-stone-500">
                  <span className="flex items-center gap-1">
                    <PoundSterling className="h-3 w-3" />{svc.price}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />{formatDuration(svc.duration)}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => startEdit(svc._idx)}
                  className="p-1.5 rounded-md hover:bg-white text-stone-400 hover:text-[#1A1A1A] transition-colors"
                  data-testid={`edit-draft-service-${svc._idx}`}
                >
                  <Edit2 className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => removeService(svc._idx)}
                  className="p-1.5 rounded-md hover:bg-red-50 text-stone-400 hover:text-red-500 transition-colors"
                  data-testid={`delete-draft-service-${svc._idx}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit modal form */}
      {draft ? (
        <div className="border border-[#D4AF37]/30 bg-[#F9F6F0] rounded-lg p-5 mb-4" data-testid="service-form">
          <h4 className="font-medium text-[#1A1A1A] text-sm mb-4">
            {editing === 'new' ? 'New Service' : 'Edit Service'}
          </h4>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-[#1A1A1A] mb-1 block">Service Name *</label>
              <Input
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                placeholder="e.g. Knotless Braids, Gel Manicure"
                className="input-zuriya"
                data-testid="draft-service-name"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#1A1A1A] mb-1 block">Description</label>
              <Textarea
                value={draft.description}
                onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                placeholder="Describe the service..."
                className="input-zuriya"
                rows={2}
                data-testid="draft-service-description"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-[#1A1A1A] mb-1 block">Price *</label>
                <div className="relative">
                  <PoundSterling className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={draft.price}
                    onChange={(e) => setDraft((d) => ({ ...d, price: e.target.value }))}
                    placeholder="0.00"
                    className="input-zuriya pl-9"
                    data-testid="draft-service-price"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-[#1A1A1A] mb-1 block">Duration *</label>
                <div className="flex gap-2">
                  <Select
                    value={String(Math.floor(draft.duration / 60))}
                    onValueChange={(v) => setDraft((d) => ({ ...d, duration: parseInt(v, 10) * 60 + (d.duration % 60) }))}
                  >
                    <SelectTrigger className="input-zuriya flex-1" data-testid="draft-service-duration-hours">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((h) => (
                        <SelectItem key={h} value={String(h)}>{h} hr{h !== 1 ? 's' : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={String(draft.duration % 60)}
                    onValueChange={(v) => setDraft((d) => ({ ...d, duration: Math.floor(d.duration / 60) * 60 + parseInt(v, 10) }))}
                  >
                    <SelectTrigger className="input-zuriya flex-1" data-testid="draft-service-duration-mins">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[0, 15, 30, 45].map((m) => (
                        <SelectItem key={m} value={String(m)}>{m} min</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-stone-400 mt-1">{formatDuration(draft.duration)}</p>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-[#1A1A1A] mb-1 block">Category *</label>
              <Select
                value={draft.category}
                onValueChange={(v) => setDraft((d) => ({ ...d, category: v }))}
              >
                <SelectTrigger className="input-zuriya" data-testid="draft-service-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {(form.categories || []).map((cat) => (
                    <SelectItem key={cat} value={cat}>{CATEGORY_LABEL_MAP[cat] || cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={cancelEdit} className="border-[#E6E4DD]" data-testid="cancel-service-btn">
                Cancel
              </Button>
              <Button
                onClick={saveService}
                className="btn-primary"
                disabled={!draft.name || !draft.price || !draft.category}
                data-testid="save-service-btn"
              >
                <Check className="h-4 w-4 mr-1" />
                {editing === 'new' ? 'Add Service' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <button
          onClick={startNew}
          className="w-full py-4 border-2 border-dashed border-[#E6E4DD] rounded-lg text-stone-500 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors flex items-center justify-center gap-2"
          data-testid="add-service-btn"
        >
          <Plus className="h-5 w-5" />
          <span className="font-medium text-sm">
            {services.length === 0 ? 'Add your first service' : 'Add a custom service'}
          </span>
        </button>
      )}

      {services.length === 0 && !draft && (
        <p className="text-xs text-stone-400 mt-3 text-center">
          You can skip this step and add services later from your dashboard.
        </p>
      )}
    </div>
  );
}
